import { FC, useEffect } from 'react';
import { DrawerHeader } from '../../drawer/DrawerHeader';
import { Button, CircularProgress, OutlinedInput, Stack, Typography } from '@mui/material';
import { z } from 'zod';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { ControlledFormField } from '../../form/ControlledFormField';
import { MetricSourceSelect } from './MetricSourceSelect';
import { FormField } from '../../form/FormField';
import { FormErrors } from '../../form/FormErrors';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { MetricDataSetSelect } from './DataSetSelect';
import { useDrawerContext } from '../../drawer/DrawerContext';
import { useModalContext } from '../../modal/ModalContext';
import { MetricTemplateSelect } from './MetricTemplateSelect';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';
import { DrawerWrapper } from '../../drawer/DrawerWrapper';
import { useSnackbar } from '../../store/snackbar';

const getMetricQuery = graphql(`
	query connections_getMetric($contentMetricId: UUID!) {
		getContentMetric(by: { id: $contentMetricId }) {
			metric {
				name
				dataSet {
					id
					name
					source {
						id
						name
						type
					}
				}
			}
		}
	}
`);

const listMetricDataSource = graphql(`
	query connections_listMetricDataSource {
		listMetricSource {
			id
			name
		}
	}
`);

const getMetricDataSetQuery = graphql(`
	query connections_getMetricDataSet($dataSetId: UUID!) {
		getMetricDataSet(by: { id: $dataSetId }) {
			metricTemplate {
				id
			}
		}
	}
`);

const updateMetricMutation = graphql(`
	mutation connections_updateMetric($contentMetricId: UUID!, $name: String!, $dataSetId: UUID!, $templateId: UUID!) {
		updateContentMetric(
			by: { id: $contentMetricId }
			data: { metric: { update: { name: $name, dataSet: { connect: { id: $dataSetId } } } } }
		) {
			ok
			errorMessage
		}
		updateMetricDataSet(by: { id: $dataSetId }, data: { metricTemplate: { connect: { id: $templateId } } }) {
			ok
			errorMessage
		}
	}
`);

const getMetricTemplateQuery = graphql(`
	query metricTemplateDescription_getMetricTemplate($metricTemplateId: UUID!) {
		getMetricTemplate(by: { id: $metricTemplateId }) {
			description
		}
	}
`);

const editMetricDrawerSchema = z.object({
	contentMetricId: z.string(),
});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		name: z.string().min(1, intl.formatMessage({ id: 'form.metric.error.empty-name' })),
		sourceId: z.string().min(1, intl.formatMessage({ id: 'form.metric.error.empty-source' })),
		dataSetId: z.string().min(1, intl.formatMessage({ id: 'form.metric.error.empty-data-set' })),
		templateId: z.string().min(1, intl.formatMessage({ id: 'form.metric.error.empty-template' })),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

type Props = z.infer<typeof editMetricDrawerSchema>;

const EditMetricDrawer: FC<Props> = ({ contentMetricId }) => {
	const intl = useIntl();
	const { closeDrawer } = useDrawerContext();
	const { setModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const { data: metricResult, isLoading: isMetricLoading } = useApiQuery(getMetricQuery, {
		enabled: contentMetricId !== '',
		variables: {
			contentMetricId: contentMetricId || '',
		},
	});

	const { data: sources } = useApiQuery(listMetricDataSource);

	const { mutate: updateMetric, isPending: isMetricUpdatePending } = useApiMutation(updateMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateContentMetric.ok && result.updateMetricDataSet.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'connections.metrics.success.update' }),
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateContentMetric.errorMessage || result.updateMetricDataSet.errorMessage,
				openSnackbar,
			});
			closeDrawer();
		},
	});

	const form = useTypedForm({
		schema: createFormSchema(intl),
	});

	// sync form initial values
	useEffect(() => {
		form.reset({
			name: metricResult?.getContentMetric?.metric?.name ?? '',
			sourceId: metricResult?.getContentMetric?.metric?.dataSet?.source?.id,
			dataSetId: metricResult?.getContentMetric?.metric?.dataSet?.id ?? '',
		});
	}, [metricResult, form]);

	const values = form.watch();

	const { data: metricDataSet, isLoading: isMetricDataSetLoading } = useApiQuery(getMetricDataSetQuery, {
		enabled: values.dataSetId !== '',
		variables: {
			dataSetId: values.dataSetId,
		},
	});

	const templateId = metricDataSet?.getMetricDataSet?.metricTemplate?.id;
	const source = sources?.listMetricSource.find(({ id }) => id === values['sourceId'])?.name;

	useEffect(() => {
		if (source !== 'Manual' && metricResult?.getContentMetric?.metric?.dataSet?.source?.name === 'Manual') {
			form.setValue('dataSetId', '');
			return;
		}
		if (source === 'Manual' && form.formState.isDirty && metricResult?.getContentMetric?.metric?.dataSet?.source?.name === 'Manual') {
			form.setValue('dataSetId', metricResult.getContentMetric.metric.dataSet.id);
			return;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [source, form]);

	useEffect(() => {
		// pre-select template based on chosen data set
		form.setValue('templateId', templateId ?? '');
	}, [form, templateId]);

	const { data: metricTemplate } = useApiQuery(getMetricTemplateQuery, {
		variables: {
			metricTemplateId: values.templateId,
		},
		enabled: values.templateId !== '',
	});

	if (isMetricLoading) {
		return (
			<DrawerWrapper height={'100%'}>
				<CircularProgress />
			</DrawerWrapper>
		);
	}

	const onSubmit = (values: FormValues) => {
		updateMetric({
			contentMetricId,
			...values,
		});
	};

	const onDelete = () => {
		setModal({
			type: 'deleteMetric',
			payload: { contentMetricId, name: metricResult?.getContentMetric?.metric?.name ?? '' },
			callback: () => {
				closeDrawer();
			},
		});
	};

	return (
		<form noValidate onSubmit={form.handleSubmit(onSubmit)}>
			<DrawerWrapper height={'100%'}>
				<DrawerHeader title={metricResult?.getContentMetric?.metric?.name} />
				<Stack width={'100%'} gap={1.5}>
					<FormField
						form={form}
						name={'name'}
						label={intl.formatMessage({ id: 'form.metric.label.name' })}
						description={intl.formatMessage({ id: 'form.metric.label.name.description' })}
						render={(props) => <OutlinedInput {...props} />}
					/>

					<ControlledFormField
						form={form}
						name={'sourceId'}
						label={intl.formatMessage({ id: 'form.metric.label.source' })}
						description={
							<FormattedMessage
								id={'form.metric.label.source.description'}
								values={{
									strong: (chunks) => (
										<Typography fontWeight={500} color={(theme) => theme.palette.error.main}>
											{chunks}
										</Typography>
									),
								}}
							/>
						}
						render={(props) => (
							<MetricSourceSelect {...props} defaultSourceName={metricResult?.getContentMetric?.metric?.dataSet?.source?.name} />
						)}
					/>

					{source !== 'Manual' && (
						<ControlledFormField
							form={form}
							name={'dataSetId'}
							label={intl.formatMessage({ id: 'form.metric.label.data-set' })}
							description={
								<FormattedMessage
									id={'form.metric.label.source.description'}
									values={{
										strong: (chunks) => (
											<Typography fontWeight={500} color={(theme) => theme.palette.error.main}>
												{chunks}
											</Typography>
										),
									}}
								/>
							}
							render={(props) => <MetricDataSetSelect {...props} sourceId={form.watch('sourceId')} />}
						/>
					)}

					<ControlledFormField
						form={form}
						name={'templateId'}
						label={intl.formatMessage({ id: 'form.metric.label.template' })}
						description={intl.formatMessage({ id: 'form.metric.label.template.description' })}
						hint={metricTemplate?.getMetricTemplate?.description ?? undefined}
						render={(props) => (
							<MetricTemplateSelect
								{...props}
								disabled={values.dataSetId === ''}
								loading={isMetricDataSetLoading}
								defaultValue={metricDataSet?.getMetricDataSet?.metricTemplate?.id}
							/>
						)}
					/>

					<Stack direction={'row'}>
						<Button
							type={'button'}
							sx={{ ml: '-0.5em', color: (theme) => theme.palette[getNightOrDayPalette(theme)][900], textDecoration: 'underline' }}
							onClick={() =>
								setModal({
									type: 'editMetricTemplate',
									payload: {
										id: values.templateId,
									},
								})
							}
						>
							<FormattedMessage id={'form.metric.edit-metric-template'} />
						</Button>
					</Stack>

					<FormErrors form={form} />
				</Stack>

				<Stack flexGrow={1} direction={'row'} justifyContent={'end'} alignItems={'end'} gap={3}>
					<Button variant={'text'} onClick={onDelete} color={'secondary'}>
						<FormattedMessage id={'form.metric.delete-metric'} />
					</Button>
					<Button variant={'contained'} type={'submit'} color={'primary'} disabled={isMetricUpdatePending}>
						<FormattedMessage id={'common.save'} />
					</Button>
				</Stack>
			</DrawerWrapper>
		</form>
	);
};

export { editMetricDrawerSchema, EditMetricDrawer };
